import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { marketingAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Send, Upload, X, ImageIcon, Building2, Mail } from 'lucide-react';

function PagePreview({ templateId, page }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    marketingAPI.previewBlob(templateId, page).then((res) => {
      const url = URL.createObjectURL(res.data);
      revoke = url;
      setSrc(url);
    }).catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, page]);
  if (!src) return <div className="h-48 bg-slate-100 rounded flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={18} /></div>;
  return <img src={src} alt={`Page ${page}`} className="w-full rounded border bg-slate-50" />;
}

export default function MarketingPersonalize() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Attachments (headshot, logo) + agency brand
  const [attachments, setAttachments] = useState([]);  // [{kind, storage_path, filename, content_type, previewUrl}]
  const [uploadingKind, setUploadingKind] = useState(null);
  const [brand, setBrand] = useState(null);          // {agency_id, agency_name, logo_path, inherited_from}
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  const [useAgencyLogo, setUseAgencyLogo] = useState(true);

  const buildDefaults = useCallback((tpl) => {
    const u = user || {};
    const defaults = {
      name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
      email: u.email || '',
      phone: u.phone || '',
      address: u.address || u.address_line1 || '',
      npn: u.agent_npn || u.npn || '',
      license: u.license_number || '',
      agency_name: u.agency_name || u.active_context?.name || '',
      full_name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
    };
    const initial = {};
    (tpl.fields || []).forEach((f) => {
      initial[f.key] = defaults[f.key] || defaults[f.key?.replace('_', '')] || '';
    });
    return initial;
  }, [user]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [tpl, brandRes] = await Promise.all([
          marketingAPI.getTemplate(id),
          marketingAPI.brandInfo().catch(() => ({ data: null })),
        ]);
        setTemplate(tpl.data);
        setValues(buildDefaults(tpl.data));
        if (brandRes?.data?.logo_path) {
          setBrand(brandRes.data);
          try {
            const blob = await marketingAPI.attachmentBlob(brandRes.data.logo_path);
            setBrandLogoUrl(URL.createObjectURL(blob.data));
          } catch (e) { console.warn('Logo preview failed (non-critical):', e); }
        }
      } catch (e) {
        console.error('Failed to load template:', e);
        toast.error('Could not load template');
        navigate('/marketing');
      } finally { setLoading(false); }
    })();
  }, [id, buildDefaults, navigate, user]);

  // Clean up blob URLs on unmount
  useEffect(() => () => {
    if (brandLogoUrl) URL.revokeObjectURL(brandLogoUrl);
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
  }, [attachments, brandLogoUrl]);

  const handleAttachmentUpload = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await marketingAPI.uploadAttachment(fd);
      setAttachments((arr) => [
        ...arr,
        { ...res.data, previewUrl: URL.createObjectURL(file) },
      ]);
      toast.success(`${kind === 'photo' ? 'Headshot' : kind === 'logo' ? 'Logo' : 'File'} uploaded`);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingKind(null); }
  };

  const removeAttachment = (idx) => {
    setAttachments((arr) => {
      const removed = arr[idx];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return arr.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    // Required field validation
    const missing = (template?.fields || []).filter((f) => f.required && !String(values[f.key] || '').trim()).map((f) => f.label);
    if (missing.length) {
      toast.error(`Missing required: ${missing.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        field_values: values,
        notes,
        attachments: attachments.map(({ kind, storage_path, filename, content_type }) => ({
          kind, storage_path, filename, content_type,
        })),
        use_agency_logo: useAgencyLogo,
      };
      await marketingAPI.placeOrder(id, payload);
      toast.success('Order submitted — KR will design your piece and email you when ready');
      navigate('/marketing/requests');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="page-content flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!template) return null;
  const pageCount = template.page_count || 1;

  return (
    <div data-testid="marketing-personalize">
      <PageHeader
        title={`Order: ${template.title}`}
        description="Fill in your details. KR will personalize the piece and notify you when it's ready."
        actions={<Link to="/marketing"><Button variant="outline"><ArrowLeft size={16} className="mr-2" />Back</Button></Link>}
      />
      <div className="page-content grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
        <Card>
          <CardHeader><CardTitle className="text-sm">Sample {pageCount > 1 ? `(${pageCount} pages)` : ''}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <div key={n}>
                {pageCount > 1 && <p className="text-xs text-slate-500 mb-1.5">{n === 1 ? 'Page 1 (Front)' : n === 2 ? 'Page 2 (Back)' : `Page ${n}`}</p>}
                <PagePreview templateId={template.template_id} page={n} />
              </div>
            ))}
            {template.description && <p className="text-sm text-slate-600 mt-2">{template.description}</p>}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm">Your Information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(template.fields || []).length === 0 ? (
                <p className="text-xs text-slate-500">This template has no form fields configured yet.</p>
              ) : (template.fields || []).map((f, idx) => (
                <div key={f.field_id || f.key || idx}>
                  <Label className="text-xs">{f.label}{f.required && ' *'}</Label>
                  {f.type === 'textarea' ? (
                    <Textarea
                      value={values[f.key] || ''}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      rows={3}
                      data-testid={`field-${f.key}`}
                    />
                  ) : (
                    <Input
                      type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'}
                      value={values[f.key] || ''}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      data-testid={`field-${f.key}`}
                    />
                  )}
                  {f.helper_text && <p className="text-[11px] text-slate-500 mt-0.5">{f.helper_text}</p>}
                </div>
              ))}
              <div>
                <Label className="text-xs">Additional notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything else KR should know?" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">Photos &amp; Logos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Agency brand */}
              {brand?.logo_path && (
                <div className="border rounded-lg p-3 bg-sky-50">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-md bg-white border flex items-center justify-center overflow-hidden">
                      {brandLogoUrl ? <img src={brandLogoUrl} alt="" className="max-w-full max-h-full object-contain" /> : <Building2 size={20} className="text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-sky-900">{brand.agency_name}</p>
                      <p className="text-[11px] text-sky-700 mt-0.5">
                        {brand.inherited_from ? <>Branding inherited from <strong>{brand.inherited_from.agency_name}</strong></> : 'Your agency brand'}
                      </p>
                      <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                        <Checkbox checked={useAgencyLogo} onCheckedChange={(c) => setUseAgencyLogo(!!c)} data-testid="use-agency-logo-checkbox" />
                        <span className="text-[11px] text-sky-900">Include this brand on my piece</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload buttons */}
              <div className="grid grid-cols-2 gap-2">
                <label className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:bg-slate-50 transition-colors">
                  <input type="file" accept="image/*" className="hidden" disabled={!!uploadingKind} onChange={(e) => handleAttachmentUpload(e, 'photo')} data-testid="upload-headshot-input" />
                  <div className="flex flex-col items-center gap-1">
                    {uploadingKind === 'photo' ? <Loader2 size={18} className="animate-spin text-slate-400" /> : <ImageIcon size={18} className="text-slate-500" />}
                    <span className="text-xs font-medium text-slate-700">Upload Headshot</span>
                    <span className="text-[10px] text-slate-400">PNG, JPG up to 10 MB</span>
                  </div>
                </label>
                <label className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:bg-slate-50 transition-colors">
                  <input type="file" accept="image/*" className="hidden" disabled={!!uploadingKind} onChange={(e) => handleAttachmentUpload(e, 'logo')} data-testid="upload-logo-input" />
                  <div className="flex flex-col items-center gap-1">
                    {uploadingKind === 'logo' ? <Loader2 size={18} className="animate-spin text-slate-400" /> : <Upload size={18} className="text-slate-500" />}
                    <span className="text-xs font-medium text-slate-700">Upload Logo</span>
                    <span className="text-[10px] text-slate-400">PNG (transparent preferred)</span>
                  </div>
                </label>
              </div>

              {/* Uploaded attachments preview */}
              {attachments.length > 0 && (
                <div className="space-y-1.5">
                  {attachments.map((a, idx) => (
                    <div key={a.storage_path} className="flex items-center gap-2 bg-slate-50 rounded p-2" data-testid={`attachment-row-${idx}`}>
                      <div className="w-10 h-10 rounded bg-white border flex items-center justify-center overflow-hidden shrink-0">
                        {a.previewUrl ? <img src={a.previewUrl} alt="" className="max-w-full max-h-full object-contain" /> : <ImageIcon size={14} className="text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{a.filename || 'attachment'}</p>
                        <p className="text-[10px] text-slate-500 capitalize">{a.kind}</p>
                      </div>
                      <button type="button" onClick={() => removeAttachment(idx)} className="text-slate-400 hover:text-rose-500" data-testid={`remove-attachment-${idx}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full" data-testid="submit-order-btn">
            {submitting ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
            Submit Order
          </Button>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <p className="font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <Mail size={12} className="text-sky-600" />How this works
            </p>
            <p>After you submit, our design team reviews your details and produces the artwork. We&apos;ll reach out if anything is missing. If your order has a cost, you&apos;ll receive a <strong>QuickBooks invoice</strong> to pay before shipping.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
